import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/global.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import UserRoleProvider from './GetUserRole';
import { alpha, createTheme, CssBaseline, ThemeProvider } from '@mui/material';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#5b5df0',
      light: '#8c82ff',
      dark: '#3c3fc3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#06b6d4',
      light: '#67e8f9',
      dark: '#0e7490',
      contrastText: '#ffffff',
    },
    success: {
      main: '#0ea5a4',
    },
    background: {
      default: '#f5f7ff',
      paper: 'rgba(255,255,255,0.88)',
    },
    text: {
      primary: '#141b34',
      secondary: '#566184',
    },
  },
  shape: {
    borderRadius: 18,
  },
  typography: {
    fontFamily: `'Inter', 'Manrope', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif`,
    h1: { fontWeight: 800, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800, letterSpacing: '-0.03em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    button: {
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: 0.15,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          colorScheme: 'light',
        },
        body: {
          background: 'var(--rf-page-bg)',
          color: '#141b34',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          width: '100%',
          maxWidth: '100% !important',
          paddingLeft: 'clamp(12px, 3vw, 32px)',
          paddingRight: 'clamp(12px, 3vw, 32px)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          backgroundImage: 'none',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(91, 93, 240, 0.12)',
          boxShadow: '0 14px 30px rgba(32, 45, 90, 0.12)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 22,
          border: '1px solid rgba(91, 93, 240, 0.14)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(247,249,255,0.9) 100%)',
          boxShadow: '0 14px 30px rgba(44, 52, 108, 0.12)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 13,
            backgroundColor: 'rgba(255,255,255,0.92)',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 13,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#5b5df0',
            borderWidth: 2,
          },
        },
        notchedOutline: {
          borderColor: 'rgba(91, 93, 240, 0.2)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 20px',
          fontWeight: 700,
          transition: 'transform 160ms ease, box-shadow 180ms ease, filter 180ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 12px 24px rgba(71, 74, 208, 0.22)',
            filter: 'saturate(1.06)',
          },
        },
        contained: {
          backgroundImage: 'linear-gradient(135deg, #5b5df0 0%, #8b5cf6 52%, #06b6d4 100%)',
          '&:hover': {
            backgroundImage: 'linear-gradient(135deg, #4749d0 0%, #7e3af2 52%, #0891b2 100%)',
          },
        },
        outlined: {
          borderColor: alpha('#5b5df0', 0.35),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'rgba(91, 93, 240, 0.08)',
            color: '#303b66',
            fontWeight: 700,
          },
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserRoleProvider>
        <App />
      </UserRoleProvider>
    </ThemeProvider>
  </React.StrictMode>
);

reportWebVitals();
